import {
    apiCalculateSubscriptionEditOffersPrice,
    apiCalculateSubscriptionOffersPrice,
    SubscriptionOfferStore
} from "../../../client/state/SubscriptionOfferStore";
import {Dispatchable, Dispatcher} from "../../../stem-core/src/base/Dispatcher";
import {SOUTHERN_THING_ALIAS} from "../../Constants"
import {iFrameState} from "../../services/IFrameState";
import {AnalyticsEventType, dispatchAnalyticsEvent} from "../../../blink-sdk/utils/AnalyticsClient";
import {LoadingSpinner} from "../../ui/LoadingSpinner";
import {
    apiCancelSubscription,
    apiChangeSubscription,
    apiSubscribe, apiSubscribeWithCds,
    SubscriptionApplyPolicy
} from "../../../client/state/SubscriptionStore";
import {Messages} from "../../Messages";
import {Router} from "../../../stem-core/src/ui/Router";
import {INLINE_EDIT_SUBSCRIPTION_URLS, INLINE_SUBSCRIPTION_URLS} from "../PaywallConstants";
import {iFrameMerchantService} from "../../services/IframeMerchantService";
import {iFrameUserDataService} from "../../services/IFrameUserDataService";
import {PaywallToast} from "../PaywallToast";
import {FLOW_CHECKPOINT, FLOW_TYPE, PANEL_TYPE, PANEL_VARIANT} from "../../../blink-sdk/Constants";
import {paymentMethodController} from "../inline-donation/PaymentMethodController";
import {PaymentMethodStore} from "../../../client/state/PaymentMethodStore";
import {CARD_SECTION_STATE} from "../inline-donation/PaymentMethodController";
import {addressController} from "./AddressController";
import {ADDRESS_SECTION_STATE} from "../pages/panels/address/AddressPanel";
import {SUBSCRIBE_PLAN_STATE, subscribePlanController} from "./SubscribePlanController";
import {collapseIframe} from "../../widget/misc/WidgetUtils";
import {ApiErrors} from "../../../client/connection/ApiErrors";
import {sendFlowCheckpoint} from "../flows/FlowController";


export async function fetchSubscriptionPrices() {
    LoadingSpinner.show();
    if (subscribePlanController.subscribePlanState === SUBSCRIBE_PLAN_STATE.editSubscription) {
        await subscribeFlowState.fetchSubscriptionEditPrices();
    } else {
        await subscribeFlowState.checkDiscountCode();
    }
    LoadingSpinner.hide();
}


class SubscribeFlowState extends Dispatchable {
    discountCodeMap = new Map();
    pendingRequests = 0;
    paymentError = null;
    instantChangeDiscountCodeMap = new Map();
    subscriptionToCancel = null;

    constructor() {
        super();

        this.attachListener(Dispatcher.Global, "collapseIframe", () => this.clear());
    }

    getSubscriptionToCancel() {
        return this.subscriptionToCancel || iFrameUserDataService.getActiveSubscription();
    }

    init() {
        if (iFrameState.panelType !== PANEL_TYPE.subscribe) {
            return;
        }
        if (iFrameState.variant === PANEL_VARIANT.editRecurring && iFrameUserDataService.getActiveSubscription()) {
            // TODO: Update the edit subscription flow to use the common payment method component.
            this.updateFromActiveSubscription();
        } else {
            // TODO: This logic is flawed. Fix the whole initial / selected logic.
            paymentMethodController.update({
                showSaveCardCheckbox: true,
                cardSectionState: CARD_SECTION_STATE.chooseCard,
                initialPaymentMethod: paymentMethodController.getInitialPaymentMethod(),
                selectedPaymentMethod: paymentMethodController.selectedPaymentMethod
                    || paymentMethodController.initialPaymentMethod,
                savePaymentMethod: true,
                getChooseCardButtonLabel: () => Messages.finalizePayment,
                getPayWithAddedCardButtonLabel: () => Messages.finalizePayment,
                getScope: () => PaymentMethodStore.Scope.RECURRING_PAYMENT,
                onSelectSuccess: () => subscribeFlowState.subscribe(),
            });

            subscribePlanController.update({
                subscribePlanState: SUBSCRIBE_PLAN_STATE.subscribe,
                selectedOffer: subscribePlanController.selectedOffer || null,
                onSelectSuccess: async () => {
                    // If we come from login page and switch to review, we want the back to take us to the plans
                    // page. Currently, this is how we achieve that without disrupting the flow in any way.
                    Router.localHistory.push(INLINE_SUBSCRIPTION_URLS.subscriptionsOverview);
                    const subscriptionOffer = subscribePlanController.selectedOffer;
                    dispatchAnalyticsEvent(AnalyticsEventType.SUBSCRIPTION_ADD_TO_CART,
                        {recurringOfferId: subscriptionOffer.id});

                    if (!subscriptionOffer.shouldCollectAddress()) {
                        await fetchSubscriptionPrices();
                        Router.changeURL(INLINE_SUBSCRIPTION_URLS.subscriptionCheckout);
                        return;
                    }

                    Router.changeURL(INLINE_SUBSCRIPTION_URLS.address);
                },
                onCancel: collapseIframe,
            });

            addressController.update({
                addressSectionState: ADDRESS_SECTION_STATE.chooseAddress,
                onSelectSuccess: async () => {
                    await fetchSubscriptionPrices();
                    Router.changeURL(INLINE_SUBSCRIPTION_URLS.subscriptionCheckout)
                },
                onSelectCancel: () => Router.back(),
                continueLabel: Messages.reviewOrder,
            });
        }
    }

    hasDiscountCodeSupport() {
        const merchant = iFrameMerchantService.getMerchant();
        return merchant && merchant.hasSubscriptionDiscountCodeEnabled();
    }

    updateFromActiveSubscription() {
        const subscription = iFrameUserDataService.getActiveSubscription();
        if (!subscription) {
            this.clear();
        } else {
            subscribePlanController.update({
                subscribePlanState: SUBSCRIBE_PLAN_STATE.editSubscription,
                selectedOffer: subscribePlanController.selectedOffer || null,
                onSelectSuccess: async () => {
                    const offer = subscribePlanController.selectedOffer;
                    console.assert(offer != null, "Selected subscription offer should not be null at this point.");

                    if (!offer.shouldCollectAddress() || (offer.shouldCollectAddress() && addressController.selectedAddress)) {
                        await fetchSubscriptionPrices();
                        Router.changeURL(INLINE_EDIT_SUBSCRIPTION_URLS.subscribe);
                        return;
                    }

                    Router.changeURL(INLINE_EDIT_SUBSCRIPTION_URLS.address);
                },
                onSelectCancel: () => Router.back(),
                applyPolicy: subscribePlanController.applyPolicy || SubscriptionApplyPolicy.NEXT_BILLING_CYCLE,
                discountCode: subscribePlanController.discountCode || subscription.getDiscount()?.getCode() || null,
                onCancel: collapseIframe,
            });

            addressController.update({
                addressSectionState: ADDRESS_SECTION_STATE.chooseAddress,
                selectedAddress: addressController.selectedAddress || subscription.getShippingAddress(),
                onSelectCancel: () => Router.back(),
                onSelectSuccess: async () => {
                    await fetchSubscriptionPrices();
                    Router.changeURL(INLINE_EDIT_SUBSCRIPTION_URLS.subscribe)
                },
                continueLabel: Messages.continue,
            });

            paymentMethodController.update({
                showSaveCardCheckbox: true,
                cardSectionState: CARD_SECTION_STATE.chooseCard,
                entry: subscription,
                initialPaymentMethod: subscription.getPaymentMethod(),
                selectedPaymentMethod: paymentMethodController.selectedPaymentMethod || subscription.getPaymentMethod(),
                savePaymentMethod: true,
                getChooseCardButtonLabel: () => Messages.updatePaymentMethod,
                getPayWithAddedCardButtonLabel: () => Messages.updatePaymentMethod,
                getScope: () => PaymentMethodStore.Scope.RECURRING_PAYMENT,
                onSelectSuccess: () => Router.changeURL(INLINE_EDIT_SUBSCRIPTION_URLS.subscribe),
            });
        }
    }

    getSubscriptionOfferPrice(offer, discountInfo = null) {
        if (discountInfo) {
            const {discountedBeforeTaxesPrice, discountValue} = discountInfo;
            return discountValue ? offer.formatPrice(discountedBeforeTaxesPrice) : offer.getPrice();
        }

         return offer.getPrice();
    }

    userHasEquivalentSubscriptionToOffer(subscriptionOffer) {
        const subscription = iFrameUserDataService.getActiveSubscription();
        if (!subscription) {
            return false;
        }
        if (!subscriptionOffer) {
            return true;
        }
        return subscription.billingPlan === subscriptionOffer.billingPlan && subscription.coverage === subscriptionOffer.coverage;
    }

    userHasEquivalentSubscriptionToSelected() {
        return subscribePlanController.selectedOffer == null || this.userHasEquivalentSubscriptionToOffer(subscribePlanController.selectedOffer);
    }

    updateEditPricesFromExistingSubscription() {
        if (this.userHasEquivalentSubscriptionToSelected()) {
            const subscription = iFrameUserDataService.getActiveSubscription();
            this.discountCodeMap.set(null, subscription.nextCyclePrice);
        }
    }

    async fetchSubscriptionEditPrices() {
        this.pendingRequests += 1;

        try {
            const {recurringOffers} = await apiCalculateSubscriptionEditOffersPrice({
                discountCode: subscribePlanController.discountCode,
                userAddressId: addressController.selectedAddress?.id,
                subscriptionId: iFrameUserDataService.getActiveSubscription().id,
            });
            this.instantChangeDiscountCodeMap.clear();
            this.discountCodeMap.clear();
            for (const info of recurringOffers) {
                const subscriptionOffer = info.recurringOfferId == null ? null : SubscriptionOfferStore.get(info.recurringOfferId);
                if (info.applyPolicy === "instantly") {
                    this.instantChangeDiscountCodeMap.set(subscriptionOffer, info);
                } else {
                    this.discountCodeMap.set(subscriptionOffer, info);
                }
            }
        } catch (error) {
            subscribePlanController.update({discountCode:  null});
        } finally {
            this.pendingRequests -= 1;
            if (!this.pendingRequests) {
                this.dispatch("requestsFinished");
            }
        }
    }

    async checkDiscountCode() {
        this.pendingRequests += 1;
        this.discountCodeMap.clear();
        try {
            const {recurringOffers} = await apiCalculateSubscriptionOffersPrice({
                discountCode: subscribePlanController.discountCode,
                merchantId: iFrameMerchantService.merchantId,
                userAddressId: addressController.selectedAddress?.id
            });
            for (const info of recurringOffers) {
                this.discountCodeMap.set(SubscriptionOfferStore.get(info.recurringOfferId), info);
            }
        } catch (error) {
            subscribePlanController.update({discountCode:  null});
            // TODO: Handle API error.
        } finally {
            this.pendingRequests -= 1;
            if (!this.pendingRequests) {
                this.dispatch("requestsFinished");
            }
        }
    }

    async changeSubscription() {
        const shippingAddress = addressController.selectedAddress;
        const {selectedOffer, discountCode, applyPolicy} = subscribePlanController;
        const discountInfo = this.getDiscountInfo(selectedOffer);
        const activeSubscription = iFrameUserDataService.getActiveSubscription();

        let subscriptionDetails = {
            subscriptionId: activeSubscription.id,
            applyPolicy,
            recurringOfferId: selectedOffer?.id,
            discountCode: discountInfo?.discountValue ? discountCode : null,
            ...(await paymentMethodController.extractPaymentMethodFields()),
        };

        if (activeSubscription.getPaymentMethod() === paymentMethodController.selectedPaymentMethod) {
            subscriptionDetails.preferredPaymentMethodId = null;
        }

        if ((!selectedOffer && activeSubscription.getShippingAddressId()) || selectedOffer?.shouldCollectAddress()) {
            subscriptionDetails.userAddressId = shippingAddress.id;
        }

        LoadingSpinner.show();
        let subscription = null;
        try {
            subscription = await apiChangeSubscription(subscriptionDetails);
        } catch (error) {
            PaywallToast.showError(error.message);
            // TODO: Handle update error.
        } finally {
            LoadingSpinner.hide();
        }

        if (!subscription || !subscription.isActive()) {
            console.warn("Subscribe error: ", Messages.subscribeFailed);
            return;
        }
        Router.changeURL(INLINE_EDIT_SUBSCRIPTION_URLS.success);
    }

    async cancel(subscription) {
        LoadingSpinner.show();
        try {
            const subscriptionId = subscription?.id || iFrameUserDataService.getActiveSubscription().id;
            await apiCancelSubscription(subscriptionId);
            Router.changeURL(INLINE_EDIT_SUBSCRIPTION_URLS.cancelSuccess);
        } catch(e) {
            console.warn("Error cancelling subscription ", e);
        } finally {
            LoadingSpinner.hide();
        }
    }

    async getSubscribeEndpointPayload() {
        const shippingAddress = addressController.selectedAddress;
        const {selectedOffer, discountCode} = subscribePlanController;
        const discountInfo = this.getDiscountInfo(selectedOffer);
        const subscriptionDetails = {
            recurringOfferId: selectedOffer.id,
            discountCode: discountInfo?.discountValue ? discountCode : null,
            metadata: {},
            ...(await paymentMethodController.extractPaymentMethodFields()),
        };

        if (selectedOffer.shouldCollectAddress()) {
            subscriptionDetails.userAddressId = shippingAddress.id;
        }

        if (iFrameMerchantService.isMerchant(SOUTHERN_THING_ALIAS)) {
            subscriptionDetails.metadata.shareName = subscribePlanController.shareName;
        }
        return subscriptionDetails;
    }

    async subscribe() {
        LoadingSpinner.show();
        const subscriptionDetails = await this.getSubscribeEndpointPayload();
        dispatchAnalyticsEvent(AnalyticsEventType.ATTEMPT_SUBSCRIBE, {recurringOfferId: subscriptionDetails.recurringOfferId});

        let subscription = null;
        try {
            if (iFrameMerchantService.isCDSPaymentMethod()) {
                subscription = await apiSubscribeWithCds(await this.getSubscribeEndpointPayload());
            } else {
                subscription = await apiSubscribe(await this.getSubscribeEndpointPayload());
            }
        } catch (error) {
            if (error?.message) {
                this.paymentError = error;
                this.dispatch("paymentError");
                Router.changeURL(INLINE_SUBSCRIPTION_URLS.subscribeFailed);
            }
        } finally {
            LoadingSpinner.hide();
        }

        if (!subscription || !subscription.isActive()) {
            console.warn("Subscribe error: ", Messages.subscribeFailed);
            return;
        }
        sendFlowCheckpoint(FLOW_TYPE.subscription, FLOW_CHECKPOINT.complete, {
            subscriptionId: subscription.id,
            offerId: subscription.recurringOfferId,
            coverageId: subscription.coverageId,
            amount: subscription.getPrice().toMainUnitString({includeSymbol: false}),
            currency: subscription.currency.isoCode,
        });
        Router.changeURL(INLINE_SUBSCRIPTION_URLS.subscribeThankYou);
    }

    getPaymentErrorMessage() {
        if (this.paymentError && this.paymentError.code === ApiErrors.CDS_NEW_ORDER_ERROR) {
            return "";
        }
        return this.paymentError?.message || "";
    }

    isCDSPaymentError() {
        return this.paymentError?.code >= 9000 && this.paymentError?.code <= 9100;
    }

    getDiscountInfo(offer) {
        return this.discountCodeMap.get(offer);
    }

    getDiscountInfoOnInstantChangePolicy(offer) {
        return this.instantChangeDiscountCodeMap.get(offer);
    }

    clear() {
        paymentMethodController.update({
            showSaveCardCheckbox: false,
            savePaymentMethod: true,
            initialPaymentMethod: null,
            selectedPaymentMethod: null,
        });
        addressController.update({
            addressSectionState: null,
            selectedAddress: null,
        });
        subscribePlanController.update({
            selectedOffer: null,
            shareName: false,
            applyPolicy: null,
            discountCode: null,
        });
        this.subscriptionToCancel = null;
        this.discountCodeMap.clear();
        this.instantChangeDiscountCodeMap.clear();
    }
}

export const subscribeFlowState = new SubscribeFlowState();
